// Practice Details > Fee Schedule: the rate the contract says each payer owes per CPT,
// the "Expected" side of the Waterfall's pricing model (David's review, 2026-10-16).
//
// One table (David, 2026-10-25): CPT, modifier, payer and its payer ID, plan, expected,
// effective dates; filter by payer or CPT; add, edit or delete a row in place. "Upload
// CSV" opens the loader in a modal (a per-payer CSV, or PracticeSuite's Export All with
// its schedule-to-payer mapping and checks); every load is a dry run first. Rows the
// older tables hold (the super_admin contract-rates card, the Cobalt pull) are shown
// read-only, because the Waterfall prices Expected from all of them as one table.
//
// Payers, codes, rates and dates only: nothing on this page is about a patient. The
// backend is the boundary (admin + practice access); the page only decides what to show.

import React, { useCallback, useEffect, useRef, useState } from "react";
import "./FeeSchedule.css";
// The Claims page's filter bar and table (David, 2026-10-25: "look more like the
// Claims tab"): its payer dropdown, and its ledger-* classes from ClaimsLedgerPage.css.
import { PayerFilter } from "../../pages/ClaimsLedgerPage";
import HomeHealthPricing from "./HomeHealthPricing";
import {
  ExportUploadForm,
  PayerGroupsCard,
  ValidationCard,
} from "./FeeScheduleGroups";

const API_URL = process.env.REACT_APP_API_URL || "";

const base = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/fee-schedule`;

export const VISIT_CPT = "VISIT";
// What a row's code is (fee_schedules.code_type): a CPT/HCPCS, or, on a home-health
// practice, a PDGM HIPPS group (a flat rate per 30-day period) / PDGM / PDGMP.
export const CODE_TYPE_LABELS = {
  cpt: "CPT / HCPCS",
  hipps: "HIPPS (per period)",
};

export const SOURCE_LABELS = {
  csv: "CSV upload",
  grid: "typed here",
  contract_rates: "Contract rates card",
  practice_details: "Practice Details tab",
  practicesuite_export: "PracticeSuite export",
};

// Where the loader's file comes from: a per-payer CSV, or PracticeSuite's "Export
// All" workbook (every schedule at once, mapped to payers by name).
export const UPLOAD_SOURCES = [
  { key: "csv", label: "Per-payer CSV" },
  { key: "practicesuite_export", label: "PracticeSuite Export All (.xlsx)" },
];

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((body && body.error) || fallback);
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

export function fetchFeeSchedule(practiceId) {
  return request(base(practiceId), {}, "Could not load the fee schedules");
}

export function saveRow(practiceId, row) {
  const target = row.id
    ? `${base(practiceId)}/rows/${row.id}`
    : `${base(practiceId)}/rows`;
  return request(
    target,
    {
      method: row.id ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(row),
    },
    "Could not save the row",
  );
}

export function deleteRow(practiceId, rowId) {
  return request(
    `${base(practiceId)}/rows/${rowId}`,
    { method: "DELETE" },
    "Could not delete the row",
  );
}

// LUPA thresholds (home-health mode, 2026-10-17): loaded like a rate sheet, by HIPPS
// prefix; the longest prefix in force wins; the CMS table applies where no row does.
const lupaBase = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/lupa-thresholds`;

export function fetchLupaThresholds(practiceId) {
  return request(
    lupaBase(practiceId),
    {},
    "Could not load the LUPA thresholds",
  );
}

export function uploadLupaSheet(practiceId, { file, effectiveFrom, write }) {
  const form = new FormData();
  form.append("file", file);
  if (effectiveFrom) form.append("effective_from", effectiveFrom);
  form.append("write", write ? "true" : "false");
  return request(
    `${lupaBase(practiceId)}/upload`,
    { method: "POST", body: form },
    "Could not read the threshold sheet",
  );
}

export function deleteLupaRow(practiceId, rowId) {
  return request(
    `${lupaBase(practiceId)}/rows/${rowId}`,
    { method: "DELETE" },
    "Could not delete the threshold",
  );
}

function LupaThresholdsCard({ practiceId, readOnly }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [from, setFrom] = useState("");
  const [preview, setPreview] = useState(null);
  const fileInput = useRef(null);

  const load = useCallback(() => {
    let cancelled = false;
    fetchLupaThresholds(practiceId)
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  useEffect(() => {
    setData(null);
    setPreview(null);
    return load();
  }, [load]);

  const run = async (write) => {
    if (busy || !file) return;
    setBusy(true);
    setError(null);
    try {
      const report = await uploadLupaSheet(practiceId, {
        file,
        effectiveFrom: from,
        write,
      });
      if (write) {
        setMessage(`Loaded: ${describeReport(report)}.`);
        setFile(null);
        setPreview(null);
        if (fileInput.current) fileInput.current.value = "";
        load();
      } else {
        setPreview(report);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteLupaRow(practiceId, row.id);
      setMessage(`Deleted the threshold for ${row.hipps_prefix}.`);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const rows = (data && data.rows) || [];
  const unavailable = data && data.available === false;
  // Only a practice with thresholds loaded has anything to see or remove here.
  if (!data || !(data.rows || []).length) return null;

  return (
    <div className="fs-card" data-testid="lupa-thresholds">
      <h2>
        LUPA thresholds{" "}
        <span className={`fs-badge ${rows.length ? "on" : "off"}`}>
          {rows.length ? `${rows.length} rows` : "CMS table only"}
        </span>
      </h2>
      <p className="fs-muted">
        The visit count below which a 30-day period is a LUPA and the payer pays
        per visit. One row per HIPPS prefix: <code>1</code> covers every group
        starting with 1, <code>1AA11</code> one group; the longest prefix in
        force wins, and the CMS case-mix table&apos;s threshold applies to any
        group no row covers. A LUPA period is its own Waterfall block, never
        &quot;underpaid&quot;.
      </p>
      <Message kind="error" text={error} onClose={() => setError(null)} />
      <Message kind="ok" text={message} onClose={() => setMessage(null)} />
      {unavailable ? (
        <div className="fs-pending" data-testid="lupa-thresholds-pending">
          LUPA thresholds are unavailable until migration {data.migration} has
          run; the CMS table&apos;s thresholds apply until then.
        </div>
      ) : null}
      {!readOnly && !unavailable ? (
        <div className="fs-form" data-testid="lupa-thresholds-upload">
          <label>
            Effective from
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPreview(null);
              }}
            />
          </label>
          <label>
            Threshold sheet (CSV: HIPPS prefix,threshold; optional from / to
            columns)
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              aria-label="Threshold sheet"
              onChange={(e) => {
                setFile(
                  e.target.files && e.target.files[0]
                    ? e.target.files[0]
                    : null,
                );
                setPreview(null);
              }}
            />
          </label>
          <div className="fs-form-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy || !file}
              onClick={() => run(false)}
            >
              {busy && !preview ? "Reading…" : "Preview"}
            </button>
            {preview ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy || preview.new + preview.changed === 0}
                onClick={() => run(true)}
                data-testid="lupa-thresholds-load"
              >
                {busy
                  ? "Loading…"
                  : `Load ${preview.new + preview.changed} threshold${preview.new + preview.changed === 1 ? "" : "s"}`}
              </button>
            ) : null}
          </div>
          {preview ? (
            <p className="fs-muted" data-testid="lupa-thresholds-preview">
              Dry run: {describeReport(preview)}.
              {preview.changes.length
                ? ` ${preview.changes
                    .map(
                      (c) =>
                        `${c.hipps_prefix}: ${c.from === null || c.from === undefined ? "" : `${c.from} → `}${c.to} (${c.action === "created" ? "new" : "changed"})`,
                    )
                    .join("; ")}`
                : ""}
            </p>
          ) : null}
        </div>
      ) : null}
      {rows.length ? (
        <table className="fs-table">
          <thead>
            <tr>
              <th>HIPPS prefix</th>
              <th className="n">Threshold (visits)</th>
              <th>From</th>
              <th>To</th>
              <th>Source</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid="lupa-threshold-row">
                <td>{r.hipps_prefix}</td>
                <td className="n">{r.threshold}</td>
                <td>{formatDay(r.effective_from)}</td>
                <td>{r.effective_to ? formatDay(r.effective_to) : "open"}</td>
                <td>{SOURCE_LABELS[r.source] || r.source}</td>
                <td className="fs-actions">
                  {!readOnly ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => remove(r)}
                      disabled={busy}
                      data-testid="lupa-threshold-delete"
                    >
                      delete
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export function uploadSheet(
  practiceId,
  { file, payerId, plan, effectiveFrom, codeType, write },
) {
  const form = new FormData();
  form.append("file", file);
  form.append("payer_id", payerId);
  if (plan) form.append("plan", plan);
  if (effectiveFrom) form.append("effective_from", effectiveFrom);
  if (codeType) form.append("code_type", codeType);
  form.append("write", write ? "true" : "false");
  return request(
    `${base(practiceId)}/upload`,
    { method: "POST", body: form },
    write ? "Could not load the rate sheet" : "Could not read the rate sheet",
  );
}

export function describeReport(report) {
  if (!report) return "";
  const parts = [
    `${report.rows_parsed} rows parsed`,
    `${report.new} new`,
    `${report.changed} changed`,
    `${report.unchanged} unchanged`,
  ];
  return parts.join(", ");
}

function formatDay(value) {
  if (!value) return "";
  const [y, m, d] = value.split("-");
  return `${m}/${d}/${y}`;
}

const EMPTY_DRAFT = {
  payer_id: "",
  plan: "",
  cpt: "",
  code_type: "cpt",
  modifier: "",
  allowed: "",
  effective_from: "",
  effective_to: "",
  notes: "",
};

function Message({ kind, text, onClose }) {
  if (!text) return null;
  return (
    <div
      className={`fs-message ${kind}`}
      role={kind === "error" ? "alert" : "status"}
    >
      <span>{text}</span>
      <button
        type="button"
        className="message-close"
        aria-label="Dismiss"
        onClick={onClose}
      >
        ×
      </button>
    </div>
  );
}

export default function FeeSchedulePage({ selectedPractice, readOnly }) {
  const practiceId = selectedPractice && selectedPractice.id;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  // Payer names ticked in the Claims-style dropdown; none ticked is every payer.
  const [payerFilter, setPayerFilter] = useState([]);
  const [cptFilter, setCptFilter] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  // The upload form.
  const [upPayer, setUpPayer] = useState("");
  const [upPlan, setUpPlan] = useState("");
  const [upFrom, setUpFrom] = useState("");
  const [upCodeType, setUpCodeType] = useState("cpt");
  const [upSource, setUpSource] = useState("csv");
  // Bumped when the export loads or the mapping changes, so the cards reload.
  const [groupsKey, setGroupsKey] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInput = useRef(null);
  // The grid: the row being edited (by id, or "new") and its draft.
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const current = useRef(practiceId);
  current.current = practiceId;

  const load = useCallback(() => {
    if (!practiceId) return () => {};
    let cancelled = false;
    fetchFeeSchedule(practiceId)
      .then((body) => {
        if (!cancelled && current.current === practiceId) setData(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  useEffect(() => {
    setData(null);
    setError(null);
    setMessage(null);
    setPreview(null);
    setEditing(null);
    setPayerFilter([]);
    setCptFilter("");
    setShowUpload(false);
    return load();
  }, [load]);

  if (!practiceId) {
    return (
      <div className="fs-page">
        <p>Select a practice to see its fee schedules.</p>
      </div>
    );
  }

  const payers = (data && data.payers) || [];
  const payerOf = (id) => payers.find((x) => String(x.id) === String(id));
  const payerName = (id) => {
    const p = payerOf(id);
    return p ? p.payer_name : `Payer ${id}`;
  };
  const payerCode = (id) => {
    const p = payerOf(id);
    return (p && p.payer_code) || "—";
  };
  const cptQuery = cptFilter.trim().toUpperCase();
  const shown = (r) =>
    (!payerFilter.length ||
      payerFilter.includes(r.payer_name || payerName(r.practice_payer_id))) &&
    (!cptQuery || String(r.cpt || "").toUpperCase().startsWith(cptQuery));
  const filtered = payerFilter.length > 0 || cptQuery !== "";
  const rows = ((data && data.rows) || []).filter(shown);
  const legacy = ((data && data.legacy_rows) || []).filter(shown);
  const unavailable = data && data.available === false;
  // A practice whose claims bill periods loads HIPPS rates too; everyone else sees CPT rows only.
  const homeHealth = !!(data && data.bills_periods);

  const resetUpload = () => {
    setFile(null);
    setPreview(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const runUpload = async (write) => {
    if (busy || !file || !upPayer) return;
    setBusy(true);
    setError(null);
    try {
      const report = await uploadSheet(practiceId, {
        file,
        payerId: upPayer,
        plan: upPlan,
        effectiveFrom: upFrom,
        codeType: homeHealth ? upCodeType : "cpt",
        write,
      });
      if (current.current !== practiceId) return;
      if (write) {
        setMessage(
          `Loaded for ${report.payer.payer_name}: ${describeReport(report)}.`,
        );
        resetUpload();
        load();
      } else {
        setPreview(report);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (row) => {
    setEditing(row ? row.id : "new");
    setDraft(
      row
        ? {
            payer_id: String(row.practice_payer_id),
            plan: row.plan || "",
            cpt: row.cpt,
            code_type: row.code_type || "cpt",
            modifier: row.modifier || "",
            allowed: row.allowed,
            effective_from: row.effective_from || "",
            effective_to: row.effective_to || "",
            notes: row.notes || "",
          }
        : {
            ...EMPTY_DRAFT,
            payer_id: (() => {
              const only =
                payerFilter.length === 1 &&
                payers.find((x) => x.payer_name === payerFilter[0]);
              if (only) return String(only.id);
              return payers[0] ? String(payers[0].id) : "";
            })(),
          },
    );
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        payer_id: draft.payer_id,
        plan: draft.plan,
        cpt: draft.cpt,
        code_type: homeHealth ? draft.code_type || "cpt" : "cpt",
        modifier: draft.modifier,
        allowed: draft.allowed,
        effective_from: draft.effective_from,
        effective_to: draft.effective_to,
        notes: draft.notes,
      };
      if (editing !== "new") body.id = editing;
      await saveRow(practiceId, body);
      setMessage(editing === "new" ? "Row added." : "Row saved.");
      setEditing(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteRow(practiceId, row.id);
      setMessage(`Deleted ${row.cpt} for ${payerName(row.practice_payer_id)}.`);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const field = (name, props = {}) => (
    <input
      aria-label={name.replace("_", " ")}
      value={draft[name]}
      onChange={(e) => setDraft({ ...draft, [name]: e.target.value })}
      {...props}
    />
  );

  const editorRow = (
    <tr className="fs-editor" data-testid="fee-schedule-editor">
      {homeHealth ? (
        <td>
          <select
            aria-label="code type"
            value={draft.code_type || "cpt"}
            onChange={(e) => setDraft({ ...draft, code_type: e.target.value })}
          >
            {Object.entries(CODE_TYPE_LABELS).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </td>
      ) : null}
      <td>
        {field("cpt", {
          placeholder:
            homeHealth && draft.code_type === "hipps"
              ? "1AA11, PDGM or PDGMP"
              : "99213",
          className: "fs-short",
        })}
      </td>
      <td>{field("modifier", { placeholder: "—", className: "fs-mod" })}</td>
      <td>
        <select
          aria-label="payer"
          className="fs-payer-select"
          value={draft.payer_id}
          onChange={(e) => setDraft({ ...draft, payer_id: e.target.value })}
          disabled={editing !== "new"}
        >
          <option value="">Select a payer…</option>
          {payers.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.payer_name}
            </option>
          ))}
        </select>
      </td>
      <td>{draft.payer_id ? payerCode(draft.payer_id) : ""}</td>
      <td>{field("plan", { placeholder: "any plan", className: "fs-plan" })}</td>
      <td className="n">
        {field("allowed", {
          placeholder: "0.00",
          className: "fs-short",
          inputMode: "decimal",
        })}
      </td>
      <td>{field("effective_from", { type: "date" })}</td>
      <td>{field("effective_to", { type: "date" })}</td>
      <td className="fs-actions">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={
            busy ||
            !draft.payer_id ||
            !draft.cpt ||
            draft.allowed === "" ||
            !draft.effective_from
          }
        >
          {busy ? "Saving…" : "Save"}
        </button>{" "}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setEditing(null)}
          disabled={busy}
        >
          Cancel
        </button>
      </td>
    </tr>
  );

  const columns = homeHealth ? 10 : 9;

  const uploadForm = (
    <>
      <div className="fs-source-tabs" role="tablist" aria-label="Source">
        {UPLOAD_SOURCES.map((src) => (
          <button
            key={src.key}
            type="button"
            role="tab"
            aria-selected={upSource === src.key}
            className={`fs-source-tab ${upSource === src.key ? "active" : ""}`}
            onClick={() => {
              setUpSource(src.key);
              setPreview(null);
            }}
            data-testid={`fee-schedule-source-${src.key}`}
          >
            {src.label}
          </button>
        ))}
      </div>
      {upSource === "practicesuite_export" ? (
        <>
          <ExportUploadForm
            practiceId={practiceId}
            busy={busy}
            setBusy={setBusy}
            setError={setError}
            setMessage={setMessage}
            onLoaded={() => {
              setGroupsKey((k) => k + 1);
              load();
            }}
          />
          {!homeHealth ? (
            <>
              <PayerGroupsCard
                practiceId={practiceId}
                payers={payers}
                readOnly={readOnly}
                reloadKey={groupsKey}
                onError={setError}
              />
              <ValidationCard
                practiceId={practiceId}
                reloadKey={groupsKey}
                onError={setError}
              />
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="fs-form">
            <label>
              Payer
              <select
                value={upPayer}
                onChange={(e) => {
                  setUpPayer(e.target.value);
                  setPreview(null);
                }}
              >
                <option value="">Select a payer…</option>
                {payers.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.payer_name}
                  </option>
                ))}
              </select>
            </label>
            {homeHealth ? (
              <label>
                Code type
                <select
                  aria-label="upload code type"
                  value={upCodeType}
                  onChange={(e) => {
                    setUpCodeType(e.target.value);
                    setPreview(null);
                  }}
                >
                  {Object.entries(CODE_TYPE_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Plan (optional)
              <input
                type="text"
                value={upPlan}
                placeholder="every plan"
                onChange={(e) => {
                  setUpPlan(e.target.value);
                  setPreview(null);
                }}
              />
            </label>
            <label>
              Effective from
              <input
                type="date"
                value={upFrom}
                onChange={(e) => {
                  setUpFrom(e.target.value);
                  setPreview(null);
                }}
              />
            </label>
            <label>
              Rate sheet (CSV: CPT,allowed or CPT,modifier,allowed; optional
              plan and date columns)
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                aria-label="Rate sheet"
                onChange={(e) => {
                  setFile(
                    e.target.files && e.target.files[0]
                      ? e.target.files[0]
                      : null,
                  );
                  setPreview(null);
                }}
              />
            </label>
            <div className="fs-form-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy || !file || !upPayer}
                onClick={() => runUpload(false)}
              >
                {busy && !preview ? "Reading…" : "Preview"}
              </button>
              {preview ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy || preview.new + preview.changed === 0}
                  onClick={() => runUpload(true)}
                  data-testid="fee-schedule-load"
                >
                  {busy
                    ? "Loading…"
                    : `Load ${preview.new + preview.changed} rate${preview.new + preview.changed === 1 ? "" : "s"}`}
                </button>
              ) : null}
            </div>
          </div>
          {preview ? (
            <div className="fs-preview" data-testid="fee-schedule-preview">
              <div>
                Dry run for {preview.payer.payer_name}:{" "}
                {describeReport(preview)}. Rows without a date take effect{" "}
                {formatDay(preview.effective_from)}.
              </div>
              {preview.changes.length ? (
                <ul>
                  {preview.changes.slice(0, 50).map((c) => (
                    <li
                      key={`${c.row}-${c.cpt}-${c.modifier || ""}-${c.plan || ""}`}
                    >
                      {c.cpt}
                      {c.modifier ? ` ${c.modifier}` : ""}
                      {c.plan ? ` (${c.plan})` : ""}:{" "}
                      {c.from ? `${c.from} → ` : ""}
                      {c.to} ({c.action === "created" ? "new" : "changed"})
                    </li>
                  ))}
                  {preview.changes.length > 50 ? (
                    <li>…and {preview.changes.length - 50} more</li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </>
  );

  return (
    <div className="fs-page" data-testid="fee-schedule-page">
      <h3 className="practice-rules-section-title">Fee schedule</h3>
      <p className="fs-lead">
        The rate each payer owes per CPT. The Waterfall uses it as{" "}
        <strong>Expected</strong>, so a payment below it shows as underpaid. A
        blank plan or modifier matches any; CPT <code>{VISIT_CPT}</code> is a
        bundled per-visit rate.
      </p>

      <Message kind="error" text={error} onClose={() => setError(null)} />
      <Message kind="ok" text={message} onClose={() => setMessage(null)} />
      {unavailable ? (
        <div
          className="fs-pending"
          data-testid="fee-schedule-migration-pending"
        >
          Fee schedules are unavailable until migration {data.migration} has
          run; the Waterfall prices Expected at the allowed until then.
        </div>
      ) : null}

      {homeHealth ? (
        <HomeHealthPricing practiceId={practiceId} readOnly={readOnly} />
      ) : null}

      {!homeHealth ? (
      <div data-testid="fee-schedule-grid">
        <div className="ledger-filter-bar fs-filter-bar">
          <PayerFilter
            value={payerFilter}
            options={payers.map((p) => p.payer_name)}
            onChange={setPayerFilter}
          />
          <input
            type="text"
            className="ledger-payer-search"
            value={cptFilter}
            placeholder={homeHealth ? "Search code..." : "Search CPT..."}
            aria-label="CPT filter"
            onChange={(e) => setCptFilter(e.target.value)}
          />
          {filtered ? (
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                setPayerFilter([]);
                setCptFilter("");
              }}
            >
              Clear
            </button>
          ) : null}
          <span className="fs-grid-spacer" />
          {!readOnly && !unavailable ? (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => startEdit(null)}
                disabled={busy || editing !== null}
                data-testid="fee-schedule-add"
              >
                Add a row
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setShowUpload(true)}
                data-testid="fee-schedule-upload-open"
              >
                Upload CSV
              </button>
            </>
          ) : null}
        </div>
        <div className="ledger-table-wrapper">
          <table className="ledger-table fs-rates-table">
            <thead>
              <tr>
                {homeHealth ? <th>Code type</th> : null}
                <th>{homeHealth ? "Code" : "CPT"}</th>
                <th className="fs-col-mod">Modifier</th>
                <th className="fs-col-payer">Payer</th>
                <th>Payer ID</th>
                <th>Plan</th>
                <th className="n">Expected</th>
                <th>From</th>
                <th>To</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {editing === "new" ? editorRow : null}
              {rows.map((r) =>
                editing === r.id ? (
                  <React.Fragment key={r.id}>{editorRow}</React.Fragment>
                ) : (
                  <tr key={r.id} data-testid="fee-schedule-row">
                    {homeHealth ? (
                      <td data-testid="fee-schedule-code-type">
                        {CODE_TYPE_LABELS[r.code_type] ||
                          r.code_type ||
                          "CPT / HCPCS"}
                      </td>
                    ) : null}
                    <td>{r.cpt}</td>
                    <td>{r.modifier || "—"}</td>
                    <td>{r.payer_name || payerName(r.practice_payer_id)}</td>
                    <td>{payerCode(r.practice_payer_id)}</td>
                    <td>{r.plan || "any"}</td>
                    <td className="n">{r.allowed}</td>
                    <td>{formatDay(r.effective_from)}</td>
                    <td>{r.effective_to ? formatDay(r.effective_to) : "open"}</td>
                    <td className="fs-actions">
                      {!readOnly ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => startEdit(r)}
                            disabled={busy || editing !== null}
                          >
                            edit
                          </button>{" "}
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => remove(r)}
                            disabled={busy || editing !== null}
                            data-testid="fee-schedule-delete"
                          >
                            delete
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ),
              )}
              {legacy.map((r, i) => (
                <tr
                  key={`legacy-${i}`}
                  className="muted"
                  data-testid="fee-schedule-legacy-row"
                  title={`From the ${SOURCE_LABELS[r.source] || r.source}: edit it where it was loaded`}
                >
                  {homeHealth ? <td /> : null}
                  <td>{r.cpt}</td>
                  <td>{r.modifier || "—"}</td>
                  <td>{r.payer_name || payerName(r.practice_payer_id)}</td>
                  <td>{payerCode(r.practice_payer_id)}</td>
                  <td>{r.plan || "any"}</td>
                  <td className="n">{r.allowed}</td>
                  <td>{formatDay(r.effective_from)}</td>
                  <td>{r.effective_to ? formatDay(r.effective_to) : "open"}</td>
                  <td className="fs-muted">read-only</td>
                </tr>
              ))}
              {!rows.length && !legacy.length && editing !== "new" ? (
                <tr>
                  <td colSpan={columns} className="fs-muted">
                    {filtered
                      ? "No rows match."
                      : "No rates yet. Upload a CSV or add a row."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      ) : null}

      {homeHealth ? (
        <LupaThresholdsCard practiceId={practiceId} readOnly={readOnly} />
      ) : null}

      {showUpload && !readOnly ? (
        <div className="modal-overlay" onClick={() => setShowUpload(false)}>
          <div
            className="create-modal fs-upload-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="fee-schedule-upload"
          >
            <div className="fs-modal-head">
              <h2>Upload rates</h2>
              <button
                type="button"
                className="message-close-sm"
                aria-label="Close"
                onClick={() => setShowUpload(false)}
              >
                &times;
              </button>
            </div>
            <Message kind="error" text={error} onClose={() => setError(null)} />
            <Message kind="ok" text={message} onClose={() => setMessage(null)} />
            {uploadForm}
          </div>
        </div>
      ) : null}
    </div>
  );
}
